import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Subscription } from 'rxjs';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { RootState } from '../../../store/store';
import { useService } from '../../../services/services';
import { FormMode } from '../../../enums/form-mode';
import { MlResourceItem } from './interfaces/ml-resource-item';
import DashboardLoading from '../../dashboard/components/dashboard-loading';
import StaticTable, {
  StaticTableColumn,
} from '../../../components/common/table/static-table';
import { DecimalInputComponent } from '../../../components/form/elements/decimal';
import { TableColumnGroup } from '../../../components/common/table/table';
import { useNotes } from './components/use-notes';
import { SchemaContext, StepProps } from './create';
import DecimalInputWithBlurHandler from './components/decimal-input-with-blur-handler';
import css from './index.module.css';

export default function StepMlResources({
  mode,
  setIsChildLoading,
}: StepProps) {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.vaad_liz_reports_v2.ml_resources.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const { farmId, id } = useParams<{ farmId: string; id: string }>();
  const { vaadLizReportV2Service: vaadLizReportService } = useService();
  const [tableData, setTableData] = useState<{ rows: MlResourceItem[] } | null>(
    null
  );
  const { getNotesElement, modal } = useNotes('ml_resources', mode);
  const { setSchema } = useContext(SchemaContext);
  const form = useFormContext();
  const { setValue, reset } = form;
  const { isMLTotalsChanged } = useSelector((state: RootState) => ({
    isMLTotalsChanged: state.global.isMLTotalsChanged,
  }));

  const calculateTotals = useCallback(
    (resources: MlResourceItem[]) => {
      if (resources) {
        Object.values(resources).forEach((resource: MlResourceItem) => {
          let n = ((resource.n_default as number) * resource.quantity) / 100;
          if (resource.n_corr != null && resource.n_corr >= 0) {
            n = n * resource.n_corr * 0.01;
          }
          if (n.toFixed(2) !== resource.n) {
            setValue(`ml_resources.${resource.id}.n`, n.toFixed(2));
          }

          let k2o =
            ((resource.k2o_default as number) * resource.quantity) / 100;
          if (resource.k2o_corr != null && resource.k2o_corr >= 0) {
            k2o = k2o * resource.k2o_corr * 0.01;
          }
          if (k2o.toFixed(2) !== resource.k2o) {
            setValue(`ml_resources.${resource.id}.k2o`, k2o.toFixed(2));
          }

          let p2o5 =
            ((resource.p2o5_default as number) * resource.quantity) / 100;
          if (resource.p2o5_corr != null && resource.p2o5_corr >= 0) {
            p2o5 = p2o5 * resource.p2o5_corr * 0.01;
          }
          if (p2o5.toFixed(2) !== resource.p2o5) {
            setValue(`ml_resources.${resource.id}.p2o5`, p2o5.toFixed(2));
          }
        });

        const totals = Object.values(resources).reduce(
          (acc: number, resource: MlResourceItem) => {
            return resource.unit === 't'
              ? acc + (Number(resource?.n) || 0) * 1000
              : acc + (Number(resource?.n) || 0);
          },
          0
        );

        setValue(
          'farm_n_volume[0].total_farm_n_volume',
          (totals / 1000).toFixed(2)
        );
      }
    },
    [setValue]
  );

  const createSchema = useCallback(() => {
    return yup.object().shape({});
  }, []);

  const sortMlResourceItems = useCallback(
    (a: MlResourceItem, b: MlResourceItem) => {
      const typeComparison = a.product_type_value.localeCompare(
        b.product_type_value,
        'lv'
      );
      if (typeComparison !== 0) return typeComparison;

      const nameComparison = a.product_name.localeCompare(b.product_name, 'lv');
      if (nameComparison !== 0) return nameComparison;

      return a.id.localeCompare(b.id);
    },
    []
  );

  useEffect(() => {
    setIsChildLoading(true);

    const subscription: Subscription = vaadLizReportService
      .getMlResources(farmId, id)
      .subscribe(
        ({ ml_resources, farm_n_volume }) => {
          ml_resources.sort(sortMlResourceItems);

          const formValues = {
            ml_resources: {},
            farm_n_volume: farm_n_volume,
          };
          const tableData: MlResourceItem[] = ml_resources.map(
            (row: MlResourceItem) => {
              const name = `ml_resources.${row.id}`;
              formValues.ml_resources[row.id] = { ...row };

              const tableRow = { ...row };
              tableRow.n_default = (
                <DecimalInputComponent
                  name={name + '.n_default'}
                  disabled={mode === FormMode.view}
                  negative={false}
                />
              );
              tableRow.n = (
                <DecimalInputComponent name={name + '.n'} disabled />
              );
              tableRow.p2o5_default = (
                <DecimalInputComponent
                  name={name + '.p2o5_default'}
                  disabled={mode === FormMode.view}
                  negative={false}
                />
              );
              tableRow.p2o5 = (
                <DecimalInputComponent name={name + '.p2o5'} disabled />
              );
              tableRow.k2o_default = (
                <DecimalInputComponent
                  name={name + '.k2o_default'}
                  disabled={mode === FormMode.view}
                  negative={false}
                />
              );
              tableRow.k2o = (
                <DecimalInputComponent name={name + '.k2o'} disabled />
              );
              tableRow.notes_edit = getNotesElement(name, tableRow);

              return { ...tableRow };
            }
          );

          setSchema(createSchema());
          setTableData({ rows: tableData });
          setIsChildLoading(false);
          setValue('ml_resources', formValues.ml_resources);
          setValue('farm_n_volume', formValues.farm_n_volume);
        },
        (error) => {
          console.error('Error fetching data', error);
          setIsChildLoading(false);
        }
      );

    return () => {
      subscription.unsubscribe();
    };
  }, [
    createSchema,
    farmId,
    getNotesElement,
    id,
    mode,
    reset,
    setIsChildLoading,
    setSchema,
    setValue,
    sortMlResourceItems,
    vaadLizReportService,
  ]);

  const watchedNFields = useWatch({ name: 'ml_resources' });

  useEffect(() => {
    calculateTotals(watchedNFields);
  }, [calculateTotals, watchedNFields]);

  const watchedTotals = useWatch({
    name: 'farm_n_volume[0].total_farm_n_volume',
  });

  useEffect(() => {
    const currentValue = form.getValues(
      'farm_n_volume[0].planned_farm_n_volume'
    );
    if (!isMLTotalsChanged && currentValue !== watchedTotals) {
      setValue('farm_n_volume[0].planned_farm_n_volume', watchedTotals);
    }
  }, [form, isMLTotalsChanged, setValue, watchedTotals]);

  const columnGroups: TableColumnGroup<MlResourceItem>[] = [
    {
      header: tp('composition'),
      startsAtAccessor: 'n_default',
      colspan: 3,
    },
    {
      header: tp('composition_total'),
      startsAtAccessor: 'n',
      colspan: 3,
    },
  ];

  const columns = useCallback(() => {
    const baseColumns: StaticTableColumn<MlResourceItem>[] = [
      {
        accessor: 'product_category',
        header: tp('product_category'),
        className: css['strong'],
      },
      { accessor: 'product_type_value', header: tp('product_type_value') },
      { accessor: 'product_name', header: tp('product_name') },
      { accessor: 'quantity', header: tp('quantity') },
      { accessor: 'unit', header: tp('unit') },
      { accessor: 'n_default', header: tp('n') },
      { accessor: 'p2o5_default', header: tp('p2o5') },
      { accessor: 'k2o_default', header: tp('k2o') },
      { accessor: 'n', header: tp('n') },
      { accessor: 'p2o5', header: tp('p2o5') },
      { accessor: 'k2o', header: tp('k2o') },
      { accessor: 'notes_edit', header: tp('notes') },
    ];

    return baseColumns;
  }, [tp]);

  return !tableData ? (
    <DashboardLoading />
  ) : (
    <>
      <div className={`${css['table']} ${css['ml-table']}`}>
        <StaticTable<MlResourceItem>
          data={tableData}
          keyAccessor="id"
          columns={columns()}
          columnGroups={columnGroups}
          fixedHeight
        />
      </div>

      <div className={css['totals']}>
        <label htmlFor="farm_n_volume[0].total_farm_n_volume">
          {tp('total_farm_n_volume')}
        </label>
        <DecimalInputComponent
          name="farm_n_volume[0].total_farm_n_volume"
          readonly
          disabled
        />
      </div>

      <div className={css['totals']}>
        <label htmlFor="farm_n_volume[0].planned_farm_n_volume">
          {tp('planned_farm_n_volume')}
        </label>
        <DecimalInputWithBlurHandler
          name="farm_n_volume[0].planned_farm_n_volume"
          disabled={mode === FormMode.view}
        />
      </div>

      {modal}
    </>
  );
}
